import { CSSProperties, useEffect, useRef, useState } from 'react';
import { TabComponent, TabsComponent } from '@vivli/shared/components';
import { GridApi } from 'ag-grid-community';
import { IDataRequestSummary } from '@vivli/features/data-requests/infrastructure/interface';
import { DataRequestListTabEnum } from '@vivli/features/data-requests/infrastructure/enum';
import { Size } from '@vivli/shared/theme';
import { ITab } from '@vivli/shared/infrastructure/interface';
import { useCsvExportHook } from '@vivli/shared/infrastructure/hook';
import { useAssignedAppTypeContext, useDataRequestsApiContext, useModalApiContext } from '@vivli/core/infrastructure/context';
import { DataRequestsGridComponent } from './data-requests-grid.component';
import { getDataRequestServerSort, PagedDataRequestsGridComponent } from './paged-data-requests-grid.component';
import { dataRequestExportColumnKeys, dataRequestExportHeaders, mapDataRequestSummaryToExportRow } from './data-requests-grid-columns.hook';
import { BehaviorSubject } from 'rxjs';
import { first } from 'rxjs/operators';
import { ExportCsvButtonView } from '@vivli/shared/views';

const exportButtonStyle: CSSProperties = {
    right: '10px',
    '@media screen and (maxWidth: 1480px)': {
        position: 'initial'
    }
} as CSSProperties;

const searchInputStyle: CSSProperties = {
    flex: 'auto',
    resize: 'none',
    padding: Size.PADDING,
    borderRadius: '8px',
    borderColor: 'transparent',
    boxShadow: 'rgba(0, 0, 0, 0.2) 0px 1px 20px, rgba(0, 0, 0, 0.2) 0px 1px 2px',
    color: 'black',
    position: 'absolute',
    top: '20px',
    right: '40px'
};

const containerStyle: CSSProperties = {
    padding: `0 ${Size.INSET_SIZE}px`,
    height: '100%',
    position: 'relative',
    backgroundColor: 'rgb(233, 234, 239)'
};

interface DataRequestsPageComponentProps {
    title?: string;
    subHeader?: string;
    tabs?: ITab<IDataRequestSummary[]>[];
    defaultTab?: string;
    onRowClick?: (dataRequest: IDataRequestSummary) => void;
    onTabClick?: (tab: ITab<IDataRequestSummary[]>) => void;
    showExport?: boolean;
    displayType?: 'default' | 'myrequests';
    hideFloatingFilter?: boolean;
    isPresumedResearcher?: boolean;
    // DEV-1003: when set, every tab pages, searches and exports on the server instead of loading
    // tab.data. serverTabs maps each tab id to the server's tab.
    serverPaging?: {
        assignedAppType: string;
        serverTabs: Record<string, DataRequestListTabEnum>;
    };
}

const SEARCH_DEBOUNCE_MS = 300;
const EXPORT_PAGE_SIZE = 500;

const gridApis: { gridApi: GridApi; id: string }[] = [];

export const DataRequestsPageComponent = ({
    title,
    subHeader,
    tabs,
    defaultTab,
    onRowClick,
    onTabClick,
    showExport,
    displayType,
    hideFloatingFilter,
    isPresumedResearcher,
    serverPaging
}: DataRequestsPageComponentProps) => {
    const [activeTabs, setActiveTabs] = useState<ITab<IDataRequestSummary[]>[]>(null);
    const [activeTabIndex, setActiveTabIndex] = useState(0);
    const [searchText, setSearchText] = useState('');
    const [serverSearch, setServerSearch] = useState('');
    const isExportingRef = useRef(false);
    const dataRequestsApi = useDataRequestsApiContext();
    const modalService = useModalApiContext();
    const { isAmr } = useAssignedAppTypeContext();
    const { exportRows } = useCsvExportHook();

    // wait for typing to pause, so each keystroke does not refetch every tab
    useEffect(() => {
        if (!serverPaging) {
            return;
        }

        const timeoutId = setTimeout(() => setServerSearch(searchText), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timeoutId);
    }, [searchText]);

    const getExportButton = (tab: ITab<IDataRequestSummary[]>) => {
        if (!showExport) {
            return null;
        }

        const exportBtnStyle: CSSProperties = {
            position: 'absolute',
            right: 0
        };

        return <ExportCsvButtonView style={exportBtnStyle} onClick={() => exportGridData(tab)} />;
    };

    const getExportFileName = (tab: ITab<IDataRequestSummary[]>) => {
        const date = new Date();
        const dateStr = `${date.getMonth() + 1}${date.getDate()}${date.getFullYear()}`;
        const titleStr = tab.title.replace(/\s+/g, '_').toLowerCase();
        return `${titleStr}_data_requests_${dateStr}`;
    };

    const exportGridData = (tab: ITab<IDataRequestSummary[]>) => {
        if (serverPaging) {
            exportServerPages(tab);
            return;
        }

        const gridApiToUse = gridApis.find((ga) => ga.id === tab.id)?.gridApi;
        gridApiToUse.exportDataAsCsv({ fileName: getExportFileName(tab), columnKeys: dataRequestExportColumnKeys });
    };

    // DEV-1003: the grid only holds one page, so fetch every page of the tab (with the current
    // search and the tab's sort) and build the CSV here. exportRows sanitizes each cell.
    const exportServerPages = (tab: ITab<IDataRequestSummary[]>) => {
        if (isExportingRef.current) {
            return;
        }
        isExportingRef.current = true;

        const rows: IDataRequestSummary[] = [];
        const request = {
            tab: serverPaging.serverTabs[tab.id],
            search: serverSearch.trim() || undefined,
            ...getDataRequestServerSort(tab.initialSortField ?? 'customTitle', tab.initialSortOrder ?? 'desc')
        };

        const fetchFrom = (startRow: number) => {
            dataRequestsApi
                .getDataRequestSummaryPage(serverPaging.assignedAppType, {
                    ...request,
                    startRow,
                    endRow: startRow + EXPORT_PAGE_SIZE
                })
                .pipe(first())
                .subscribe({
                    next: (page) => {
                        rows.push(...page.rows);
                        if (page.rows.length > 0 && rows.length < page.totalCount) {
                            fetchFrom(startRow + EXPORT_PAGE_SIZE);
                            return;
                        }

                        isExportingRef.current = false;
                        exportRows(
                            `${getExportFileName(tab)}.csv`,
                            rows.map((row) => mapDataRequestSummaryToExportRow(row, isAmr)),
                            dataRequestExportHeaders
                        );
                    },
                    error: (error) => {
                        isExportingRef.current = false;
                        modalService.error(`An error happened exporting Data Requests. Error received: ${error}`);
                    }
                });
        };

        fetchFrom(0);
    };

    const addGridApi = (gridApi: GridApi, title) => {
        //prevents extra gridApis from being added
        const existingIndex = gridApis.findIndex((api) => api.id === title);
        if (existingIndex >= 0) {
            gridApis[existingIndex] = { gridApi, id: title };
        } else {
            gridApis.push({ gridApi, id: title });
        }
    };

    const handleTabClick = (tab: ITab<IDataRequestSummary[]>) => {
        onTabClick && onTabClick(tab);
    };

    const handleRowClick = (dataRequest: IDataRequestSummary) => {
        onRowClick && onRowClick(dataRequest);
    };

    const handleSearchChange = (searchValue: string) => {
        if (serverPaging) {
            setSearchText(searchValue);
            return;
        }

        gridApis?.forEach(async ({ gridApi }) => {
            await gridApi.setColumnFilterModel('customTitle', {
                filterType: 'text',
                type: 'contains',
                filter: searchValue
            });
            gridApi.onFilterChanged();
        });
    };

    useEffect(() => {
        if (!tabs) {
            return;
        }

        setActiveTabs((previousTabs) =>
            tabs.map((t) => {
                const existingTab = previousTabs?.find((tab) => tab.id === t.id);
                const existingCount = existingTab?.countSubject?.getValue?.() ?? existingTab?.count ?? 0;
                const count = t.count ?? existingCount;

                existingTab?.countSubject?.next(count);

                return {
                    ...t,
                    count,
                    countSubject: existingTab?.countSubject ?? new BehaviorSubject(count)
                };
            })
        );
    }, [tabs]);

    useEffect(() => {
        if (!defaultTab || !activeTabs) {
            return;
        }

        const index = activeTabs.findIndex((t) => t.id.toLowerCase() === defaultTab.toLowerCase());

        setActiveTabIndex(index);
    }, [defaultTab, activeTabs]);

    return (
        <div style={containerStyle}>
            <input
                type="text"
                style={searchInputStyle}
                placeholder="Search data requests"
                onChange={(e) => handleSearchChange(e.target.value)}
            />
            <TabsComponent title={title} subTitle={subHeader} defaultTab={activeTabIndex}>
                {activeTabs?.map((tab, i) => {
                    const { title, data, hint, count, countSubject } = tab;
                    return (
                        <TabComponent
                            key={i}
                            title={title}
                            hint={hint}
                            count={count}
                            countSubject={countSubject}
                            onTabClick={() => handleTabClick(tab)}
                            rightHeaderContent={getExportButton(tab)}
                        >
                            {serverPaging ? (
                                <PagedDataRequestsGridComponent
                                    assignedAppType={serverPaging.assignedAppType}
                                    tab={serverPaging.serverTabs[tab.id]}
                                    search={serverSearch}
                                    isPresumedResearcher={isPresumedResearcher}
                                    onRowClick={handleRowClick}
                                    onTotalCountChange={(totalCount) => countSubject?.next(totalCount)}
                                    initialSortField={tab.initialSortField}
                                    initialSortOrder={tab.initialSortOrder}
                                />
                            ) : (
                                <DataRequestsGridComponent
                                    isPresumedResearcher={isPresumedResearcher}
                                    displayType={displayType}
                                    dataRequests={data}
                                    onRowClick={handleRowClick}
                                    onGridReady={(gridApi) => addGridApi(gridApi, tab.id)}
                                    hideFloatingFilter={hideFloatingFilter}
                                    onRowCountChange={(rowCount) => {
                                        countSubject?.next(rowCount);
                                    }}
                                    initialSortField={tab.initialSortField}
                                    initialSortOrder={tab.initialSortOrder}
                                />
                            )}
                        </TabComponent>
                    );
                })}
            </TabsComponent>
        </div>
    );
};
